import {
  decodeFileIndexRowSourceBlockId,
  isFileIndexDatabaseId,
} from '@streamlain/database-identity';

import type {
  DocsSearchResult,
  PdfPageSearchHit,
} from '../../docs-search/services/source-navigation';

export {
  type DocsBlockPayload,
  type DocsPayload,
  type DocsPdfPagePayload,
  type DocsSearchResult,
  getDocsPayloadSourceTypes,
  isDocsPdfPagePayload,
  type PdfPageSearchHit,
} from '../../docs-search/services/source-navigation';

export interface AggregatedPdfPage {
  pageIndex: number;
  attachmentBlockId: string;
  bestHit: PdfPageSearchHit;
  hitCount: number;
}

export interface AggregatedPdfAttachment {
  attachmentBlockId: string;
  totalHits: number;
  pages: AggregatedPdfPage[];
  bestHit: PdfPageSearchHit;
}

export interface AggregatedPdfDoc {
  docId: string;
  totalHits: number;
  attachments: AggregatedPdfAttachment[];
  bestHit: PdfPageSearchHit;
}

export function aggregatePdfHitsByDoc(
  pdfHits: PdfPageSearchHit[]
): AggregatedPdfDoc[] {
  const byDoc = new Map<string, PdfPageSearchHit[]>();
  for (const hit of pdfHits) {
    const list = byDoc.get(hit.docId);
    if (list) list.push(hit);
    else byDoc.set(hit.docId, [hit]);
  }
  const result: AggregatedPdfDoc[] = [];
  for (const [docId, hits] of byDoc) {
    const byAttachment = new Map<string, PdfPageSearchHit[]>();
    for (const hit of hits) {
      const list = byAttachment.get(hit.attachmentBlockId);
      if (list) list.push(hit);
      else byAttachment.set(hit.attachmentBlockId, [hit]);
    }
    const attachments: AggregatedPdfAttachment[] = [];
    for (const [attachmentBlockId, attachmentHits] of byAttachment) {
      const byPage = new Map<number, PdfPageSearchHit[]>();
      for (const hit of attachmentHits) {
        const list = byPage.get(hit.pageIndex);
        if (list) list.push(hit);
        else byPage.set(hit.pageIndex, [hit]);
      }
      const pages: AggregatedPdfPage[] = [];
      for (const pageHits of byPage.values()) {
        const bestHit = pageHits.reduce((a, b) => (b.score > a.score ? b : a));
        pages.push({
          pageIndex: bestHit.pageIndex,
          attachmentBlockId: bestHit.attachmentBlockId,
          bestHit,
          hitCount: pageHits.length,
        });
      }
      const bestHit = pages.reduce((a, b) =>
        b.bestHit.score > a.bestHit.score ? b : a
      ).bestHit;
      pages.sort((a, b) => a.pageIndex - b.pageIndex);
      attachments.push({
        attachmentBlockId,
        totalHits: attachmentHits.length,
        pages,
        bestHit,
      });
    }
    attachments.sort((a, b) => b.bestHit.score - a.bestHit.score);
    result.push({
      docId,
      totalHits: hits.length,
      attachments,
      bestHit: attachments[0].bestHit,
    });
  }
  result.sort((a, b) => b.bestHit.score - a.bestHit.score);
  return result;
}

export function filterBlockResultsShadowedByPdfHits(
  docs: DocsSearchResult[],
  pdfHits: PdfPageSearchHit[]
): DocsSearchResult[] {
  const pdfAttachmentKeys = new Set(
    pdfHits.map(hit => `${hit.docId}:${hit.attachmentBlockId}`)
  );

  return docs.filter(doc => {
    const candidateBlockIds = getSearchResultBlockIdentityCandidates(doc);
    if (candidateBlockIds.length === 0) return true;
    return !candidateBlockIds.some(blockId =>
      pdfAttachmentKeys.has(`${doc.docId}:${blockId}`)
    );
  });
}

function getSearchResultBlockIdentityCandidates(
  result: DocsSearchResult
): string[] {
  const candidates = [
    result.blockId,
    result.elementId,
    result.parentBlockId,
    result.noteBlockId,
    getFileIndexSourceBlockId(result),
  ].filter((value): value is string => typeof value === 'string' && !!value);

  return [...new Set(candidates)];
}

function getFileIndexSourceBlockId(result: DocsSearchResult) {
  const databaseId =
    result.databaseId ??
    (result.sourceType === 'database' ? result.blockId : undefined);
  if (!isFileIndexDatabaseId(databaseId)) return undefined;
  return decodeFileIndexRowSourceBlockId(result.databaseRowId);
}
